import { Button } from '@design-system/button';
import React, { FC, useCallback, useRef } from 'react';

import { CarouselProps } from '../models';

const SLIDE_DEBOUNCE_MS = 400;

interface CarouselButtonsProps
  extends Omit<
    CarouselProps,
    'items' | 'progressIndicator' | 'ariaLivePlaceholder'
  > {
  isHideControls: boolean | null;
  isAtBeginning: boolean;
  isAtEnd: boolean;
  onNextSlide: () => void;
  onPrevSlide: () => void;
}

export const CarouselButtonsBlock: FC<CarouselButtonsProps> = ({
  buttonPrev,
  buttonNext,
  isHideControls,
  isAtBeginning,
  isAtEnd,
  onNextSlide: onNextSlideProp,
  onPrevSlide: onPrevSlideProp,
}) => {
  const isAnimatingRef = useRef(false);

  const slideWithDebounce = useCallback((slideFn: () => void) => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    slideFn();
    setTimeout(() => {
      isAnimatingRef.current = false;
    }, SLIDE_DEBOUNCE_MS);
  }, []);

  const onNextSlide = useCallback(
    () => slideWithDebounce(onNextSlideProp),
    [onNextSlideProp, slideWithDebounce]
  );

  const onPrevSlide = useCallback(
    () => slideWithDebounce(onPrevSlideProp),
    [onPrevSlideProp, slideWithDebounce]
  );

  if (isHideControls) return null;

  return (
    <div className="cds-carousel__button-group">
      <Button
        disabled={isAtBeginning}
        onClick={onPrevSlide}
        variant="primary-icon"
        {...buttonPrev}
        iconPrefix={{
          props: {
            icon: 'arrowLeft',
          },
        }}
        className="cds-carousel__button-group--prev"
      >
        {buttonPrev.children}
      </Button>

      <Button
        disabled={isAtEnd}
        onClick={onNextSlide}
        variant="primary-icon"
        {...buttonNext}
        iconPrefix={{
          props: {
            icon: 'arrowRight',
          },
        }}
        className="cds-carousel__button-group--next"
      >
        {buttonNext.children}
      </Button>
    </div>
  );
};
